/**
 * TutorialShowcase — Premium renderer for tutorial / step-by-step guide layouts.
 * Palette: cyan primary + lime progress.
 * Migrated to Athena Presentation Design System (Phase 7.5).
 */
import React, { useState } from 'react';
import { usePresentationBreakpoint, AthenaTip, ActionChips, HeroPhotoPanel, MetricStrip }
  from '../presentation-system';
import { base } from '../presentation-system/tokens/colors';
import { ProseText } from './ProseText';

const C = {
  accent:    '#22d3ee',
  accentFade:'rgba(34,211,238,0.10)',
  accentBdr: 'rgba(34,211,238,0.22)',
  lime:      '#84cc16',
  limeFade:  'rgba(132,204,22,0.10)',
  limeBdr:   'rgba(132,204,22,0.22)',
  card:      base.card,
  border:    base.border,
  text:      base.text,
  textMuted: base.textMuted,
  textFaint: base.textFaint,
};

const DIFFICULTY = {
  iniciante:    { label: 'Iniciante',     color: C.lime,   bg: C.limeFade },
  intermediário:{ label: 'Intermediário', color: C.accent, bg: C.accentFade },
  intermediario:{ label: 'Intermediário', color: C.accent, bg: C.accentFade },
  avançado:     { label: 'Avançado',      color: '#f97316', bg: 'rgba(249,115,22,.1)' },
  avancado:     { label: 'Avançado',      color: '#f97316', bg: 'rgba(249,115,22,.1)' },
};

const _card = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, boxShadow: '0 2px 16px rgba(0,0,0,0.2)' };

function DifficultyBadge({ difficulty }) {
  const diff = difficulty ? difficulty.toLowerCase() : '';
  const d = DIFFICULTY[diff] || { label: difficulty || 'Guia', color: C.accent, bg: C.accentFade };
  return (
    <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: d.color, background: d.bg, border: `1px solid ${d.color}40`, borderRadius: 6, padding: '3px 10px' }}>{d.label}</span>
  );
}

function HeroDesktop({ d }) {
  const diff    = d.difficulty ? DIFFICULTY[d.difficulty.toLowerCase()] : null;
  const titleFs = (d.title || '').length > 38 ? 21 : (d.title || '').length > 24 ? 26 : 30;
  const METRICS = [
    { icon: '⏱', value: d.estimated_time || '~30 min',                       label: 'Duração' },
    { icon: '📋', value: d.step_count > 0 ? `${d.step_count} passos` : 'Guia', label: 'Passos' },
    { icon: '📊', value: diff?.label || d.difficulty || 'Guia',               label: 'Nível'   },
  ];

  return (
    <div style={{ ..._card, padding: 0, marginBottom: 14, overflow: 'hidden', display: 'flex', height: 260,
      background: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
      boxShadow: '0 4px 40px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.03) inset',
    }}>
      <HeroPhotoPanel
        heroImage={d.hero_image}
        badge="📖 TUTORIAL"
        accentColor={C.accent} accentFade={C.accentFade} accentBdr={C.accentBdr}
        placeholder="📖" placeholderLabel="Tutorial"
        title={d.title}
      />
      <div style={{ flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
        <div>
          {d.difficulty && (
            <div style={{ marginBottom: 10 }}>
              <DifficultyBadge difficulty={d.difficulty} />
            </div>
          )}
          <h1 style={{ margin: '0 0 8px', fontSize: titleFs, fontWeight: 800, color: C.text, lineHeight: 1.2, letterSpacing: '-.02em',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.title}</h1>
          {d.subtitle && <p style={{ margin: 0, fontSize: 13, color: base.textSec, lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.subtitle}</p>}
        </div>
        <MetricStrip metrics={METRICS} accentColor={C.accent} />
      </div>
    </div>
  );
}

function HeroMobile({ d }) {
  const diff    = d.difficulty ? DIFFICULTY[d.difficulty.toLowerCase()] : null;
  const METRICS = [
    { icon: '⏱', value: d.estimated_time || '~30 min',                       label: 'Duração' },
    { icon: '📋', value: d.step_count > 0 ? `${d.step_count}` : 'Guia',      label: 'Passos'  },
    { icon: '📊', value: diff?.label || d.difficulty || 'Guia',               label: 'Nível'   },
    { icon: '📖', value: 'Tutorial',                                           label: 'Tipo'    },
  ];

  return (
    <div style={{ ..._card, padding: 0, marginBottom: 12, overflow: 'hidden',
      background: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
      boxShadow: '0 4px 40px rgba(0,0,0,0.4)',
    }}>
      <div style={{ display: 'flex', minHeight: 202 }}>
        <div style={{ flex: 1, minWidth: 0, padding: '20px 10px 18px 18px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 10 }}>
            <span style={{ display: 'inline-block', alignSelf: 'flex-start', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: C.accent, background: C.accentFade, border: `1px solid ${C.accentBdr}`, borderRadius: 5, padding: '2px 8px' }}>📖 TUTORIAL</span>
            {d.difficulty && <DifficultyBadge difficulty={d.difficulty} />}
          </div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.text, lineHeight: 1.25 }}>{d.title}</h1>
          {d.subtitle && <p style={{ margin: '6px 0 0', fontSize: 12, color: base.textSec, lineHeight: 1.4,
            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{d.subtitle}</p>}
        </div>
        <HeroPhotoPanel
          heroImage={d.hero_image} isMobile
          accentColor={C.accent} accentFade={C.accentFade} accentBdr={C.accentBdr}
          placeholder="📖"
          title={d.title}
        />
      </div>
      <div style={{ borderTop: `1px solid ${C.border}`, background: 'rgba(0,0,0,0.18)' }}>
        <MetricStrip metrics={METRICS} bordered accentColor={C.accent} />
      </div>
    </div>
  );
}

function Prerequisites({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ ..._card, padding: '12px 14px', borderLeft: `3px solid ${C.accent}`, marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: C.accent, marginBottom: 8 }}>Pré-requisitos</div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 5 }}>
        {items.map((it, i) => (
          <li key={i} style={{ display: 'flex', gap: 7, fontSize: 13, color: C.text }}>
            <span style={{ color: C.accent, flexShrink: 0, marginTop: 3 }}>✓</span>
            <span style={{ lineHeight: 1.5 }}>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StepCard({ step, isMobile }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ ..._card, padding: 0, overflow: 'hidden', marginBottom: 8 }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', outline: 'none', fontFamily: 'inherit' }}
      >
        <div style={{ width: 26, height: 26, borderRadius: '50%', background: C.accentFade, border: `2px solid ${C.accentBdr}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: C.accent, flexShrink: 0 }}>
          {step.id || '?'}
        </div>
        <span style={{ flex: 1, fontSize: isMobile ? 14 : 15, fontWeight: 700, color: C.text, lineHeight: 1.3 }}>{step.title}</span>
        <span style={{ fontSize: 11, color: base.textFaint, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .18s', flexShrink: 0 }}>▼</span>
      </button>
      {open && (
        <div style={{ padding: '0 16px 14px 54px' }}>
          {step.description && (
            <div style={{ marginBottom: 8 }}>
              <ProseText text={step.description} style={{ margin: 0, fontSize: 14, color: C.textMuted, lineHeight: 1.6, whiteSpace: 'pre-wrap' }} />
            </div>
          )}
          {step.has_code && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(34,211,238,0.06)', border: `1px dashed ${C.accentBdr}`, borderRadius: 6, padding: '5px 10px', fontSize: 12, color: C.accent, marginBottom: step.observation ? 8 : 0 }}>
              💻 Contém código ou comandos
            </div>
          )}
          {step.observation && (
            <div style={{ background: C.limeFade, border: `1px solid ${C.limeBdr}`, borderRadius: 6, padding: '8px 12px', fontSize: 13, color: C.textMuted, lineHeight: 1.5 }}>
              <span style={{ fontWeight: 700, color: C.lime }}>📌 Observação: </span>{step.observation}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TutorialShowcase({ data, wp, onAction }) {
  const isMobile = usePresentationBreakpoint();
  const d = wp || data;
  if (!d) return null;

  return (
    <div style={{ color: C.text, fontFamily: 'inherit', lineHeight: 1.6, maxWidth: '100%' }}>
      {isMobile ? <HeroMobile d={d} /> : <HeroDesktop d={d} />}

      {d.context && (
        <div style={{ ..._card, padding: '12px 14px', borderLeft: `3px solid ${base.borderSoft}`, marginBottom: 12, fontSize: 14, color: C.textMuted, lineHeight: 1.6 }}>{d.context}</div>
      )}

      <Prerequisites items={d.prerequisites} />

      {(d.steps || []).length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: C.textMuted, marginBottom: 10 }}>Passo a Passo</div>
          {d.steps.map((step, i) => <StepCard key={i} step={step} isMobile={isMobile} />)}
        </div>
      )}

      {d.summary && (
        <div style={{ ..._card, padding: '12px 14px', borderLeft: `3px solid ${C.lime}`, marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: C.lime, marginBottom: 8 }}>Resumo</div>
          <p style={{ margin: 0, fontSize: 14, color: C.textMuted, lineHeight: 1.6 }}>{d.summary}</p>
        </div>
      )}

      {d.next_step && (
        <div style={{ ..._card, padding: '10px 14px', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center', borderLeft: `3px solid ${C.accentBdr}` }}>
          <span style={{ fontSize: 14, color: C.accent }}>→</span>
          <span style={{ fontSize: 14, color: C.textMuted }}><strong style={{ color: C.accent }}>Próximo: </strong>{d.next_step}</span>
        </div>
      )}

      {(d.sections || []).map((s, i) => (
        <div key={i} style={{ ..._card, padding: '12px 14px', marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: C.textMuted, marginBottom: 8 }}>{s.title}</div>
          <ProseText text={s.content} style={{ margin: 0, fontSize: 14, color: C.textMuted, lineHeight: 1.6, whiteSpace: 'pre-wrap' }} />
        </div>
      ))}

      <AthenaTip tip={d.tip} accentColor={C.accent} style={{ marginBottom: 14 }} />
      <ActionChips chips={d.follow_up_chips} onAction={onAction} accentColor={C.accent} />
    </div>
  );
}
